import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, Wrench } from "lucide-react";

import { BOIDE_AUTH_CHANGED_EVENT, getBoideToken } from "@/lib/boide-api";
import {
  featureForPath,
  getFeatureMaintenance,
  loadFeatureMaintenance,
  setFeatureMaintenanceTokenGetter,
  subscribeFeatureMaintenance,
  type FeatureMaintenance,
} from "@/lib/feature-maintenance";

setFeatureMaintenanceTokenGetter(getBoideToken);

function useFeatureMaintenanceState() {
  const state = useSyncExternalStore(
    subscribeFeatureMaintenance,
    getFeatureMaintenance,
    getFeatureMaintenance,
  );

  // Nhịp 60s + tải lại khi đăng nhập/đăng xuất (đổi tài khoản là đổi `bypass`).
  // Lượt gọi API nhận 503 cũng tự kích tải lại (`noteFeatureMaintenance` trong `unwrap`).
  useEffect(() => {
    void loadFeatureMaintenance();
    const timer = window.setInterval(() => void loadFeatureMaintenance(), 60_000);
    const onAuth = () => void loadFeatureMaintenance();
    window.addEventListener(BOIDE_AUTH_CHANGED_EVENT, onAuth);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(BOIDE_AUTH_CHANGED_EVENT, onAuth);
    };
  }, []);

  return state;
}

function defaultText(feature: FeatureMaintenance): string {
  return feature.block_reads
    ? "Tạm thời chưa dùng được, vui lòng quay lại sau ít phút."
    : "Bạn vẫn xem lại được kết quả cũ, nhưng chưa tạo lượt mới được.";
}

/**
 * Bọc `<Outlet/>` ở `__root` — bảo trì RIÊNG tính năng của trang đang mở (BE v121).
 *
 * - **Chặn cả xem** (`block_reads`) → khách thấy màn bảo trì THAY cho cả trang. Chỉ
 *   hiện banner là để họ bấm loạn trên một trang mà mọi nút đều báo lỗi.
 * - **Chỉ chặn ghi** → banner + trang vẫn mở (xem lại kết quả cũ); nút tạo mới bị BE
 *   trả 503 kèm lời nhắn.
 * - **Admin / IP được tha** (`bypass`) → KHÔNG khoá: BE cũng cho họ qua để kiểm thử.
 *   Banner nói thẳng điều đó, nếu không admin sẽ tưởng bảo trì không ăn.
 */
export function FeatureMaintenanceGate({
  pathname,
  children,
}: {
  pathname: string;
  children: ReactNode;
}) {
  const state = useFeatureMaintenanceState();
  const slug = featureForPath(pathname, state.catalog);
  const feature = slug ? state.features[slug] : undefined;
  if (!feature) return <>{children}</>;

  if (feature.block_reads && !state.bypass) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-amber-500/40 bg-card/80 p-6 text-center shadow-sm backdrop-blur">
          <Wrench className="mx-auto mb-3 h-8 w-8 text-amber-600" />
          <h2 className="text-lg font-semibold">{feature.label} đang bảo trì</h2>
          <p className="mt-2 text-sm text-foreground/75">{feature.message || defaultText(feature)}</p>
          {feature.eta ? (
            <p className="mt-1 text-xs text-foreground/60">Dự kiến: {feature.eta}</p>
          ) : null}
          <Link
            to="/"
            className="mt-5 inline-block rounded-full bg-[#E5B869] px-5 py-2 text-sm font-medium text-black hover:bg-[#f0c979]"
          >
            Về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        role="status"
        className="shrink-0 border-b border-amber-500/40 bg-amber-100/90 px-4 py-2 text-sm text-amber-950 dark:bg-amber-500/15 dark:text-amber-100"
      >
        <div className="mx-auto flex max-w-6xl items-start gap-2">
          {state.bypass ? (
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div className="min-w-0">
            <b>{feature.label} đang bảo trì.</b>{" "}
            {state.bypass
              ? `Khách đang ${feature.block_reads ? "bị khoá cả trang" : "không tạo lượt mới được"} — bạn là admin nên vẫn dùng được để kiểm thử.`
              : feature.message || defaultText(feature)}
            {feature.eta ? <span className="opacity-80"> · Dự kiến: {feature.eta}</span> : null}
          </div>
        </div>
      </div>
      {children}
    </>
  );
}
